import { z } from 'zod';
import type { BillingModel } from './payment.js';

/**
 * F38 — B2B SaaS Subscription Tiers frozen API contracts.
 *
 * Every venue carries exactly ONE subscription (per-venue — D10), one of four
 * tiers (Free / Starter / Pro / Premium). The tier drives:
 *   - which features are unlocked (the code-defined `TIER_FEATURE_FLAGS` matrix
 *     — D1/D11; the catalog stores pricing + commission only),
 *   - the venue's commission rate (the tier-derived ladder 10/8/5/3% — D2,
 *     applied by appending an F23 `venue_commission_rates` row on tier change),
 *   - whether a subscription invoice is billed (subscription_only + hybrid — D7).
 *
 * Subscription invoices ride F23's `commission_statements` state machine with
 * the two new `source` values (`venue_subscription`, `venue_hybrid` — see
 * `commission.ts COMMISSION_SOURCES`). F23's CommissionStatementWorker is
 * untouched; F38 ships a SEPARATE billing worker.
 *
 * `TierProvider` mirrors the proven provider pattern (F20 PaymentConfigProvider /
 * F33 TrainerAvailabilityProvider): interface + Stub (returns Free) + Default
 * (cache-first). F39/F40/F41 consume the INTERFACE only.
 *
 * All monetary fields are USD minor units (integer, cents).
 *
 * See plans/38_F38_b2b_subscription_tiers.md §0/§3/§7. Wave-2 sub-agents develop
 * against these contracts in parallel — DO NOT rename/drop/add fields without
 * escalating to the orchestrator first (contract-amendment protocol).
 *
 * 🔴 NOTE: F38's subscription tier (`tierCode ∈ {free,starter,pro,premium}`) is
 * a SEPARATE concept from F11's `venues.tier` listing/claim tier
 * (`tier_1/tier_2/tier_3`). Never conflate or touch `venues.tier`.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/** The four hard-coded subscription tiers (D1/D11). Stable identifiers. */
export const SUBSCRIPTION_TIER_CODES = ['free', 'starter', 'pro', 'premium'] as const;
export type SubscriptionTierCode = (typeof SUBSCRIPTION_TIER_CODES)[number];
export const SubscriptionTierCodeSchema = z.enum(SUBSCRIPTION_TIER_CODES);

/** Subscription status state machine (§5). */
export const VENUE_SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'cancelled',
  'suspended',
] as const;
export type VenueSubscriptionStatus = (typeof VENUE_SUBSCRIPTION_STATUSES)[number];
export const VenueSubscriptionStatusSchema = z.enum(VENUE_SUBSCRIPTION_STATUSES);

/** Monthly vs annual billing cadence (D3). */
export const BILLING_CADENCES = ['monthly', 'annual'] as const;
export type BillingCadence = (typeof BILLING_CADENCES)[number];
export const BillingCadenceSchema = z.enum(BILLING_CADENCES);

/**
 * The code-defined feature flags (D1/D11). The catalog table stores pricing +
 * commission only; the feature matrix lives here as a frozen record and is
 * surfaced by `TierProvider`.
 */
export const TIER_FEATURE_FLAGS_KEYS = [
  'listing',
  'bookings',
  'basic_notifications',
  'multi_court',
  'basic_analytics',
  'advanced_analytics',
  'featured_placements',
  'promotions',
  'tournament_listings',
  'priority_support',
] as const;
export type TierFeatureFlag = (typeof TIER_FEATURE_FLAGS_KEYS)[number];

/**
 * The flags a venue is shown when comparing plans (founder decision
 * 2026-10-27): only what the product delivers. `multi_court` stays advisory
 * (D6 — every tier can add courts), and no venue analytics page or
 * priority-support channel exists yet, so those four are kept in the matrix
 * as seams but not advertised. Add a flag back here when its feature ships.
 */
export const TIER_ADVERTISED_FEATURE_FLAGS: readonly TierFeatureFlag[] =
  TIER_FEATURE_FLAGS_KEYS.filter(
    (f) =>
      f !== 'multi_court' &&
      f !== 'basic_analytics' &&
      f !== 'advanced_analytics' &&
      f !== 'priority_support',
  );

/**
 * The §3 feature-gating matrix. `multi_court` is advisory at MVP (D6 — soft
 * upsell signal, not a hard block); `featured_placements` / `promotions` /
 * `tournament_listings` are HARD gates (F39/F40/F41 deny the action on a
 * lower tier). Frozen — `as const` + `Object.freeze` on each row.
 */
export const TIER_FEATURE_FLAGS: Readonly<
  Record<SubscriptionTierCode, Readonly<Record<TierFeatureFlag, boolean>>>
> = Object.freeze({
  free: Object.freeze({
    listing: true,
    bookings: true,
    basic_notifications: true,
    multi_court: false,
    basic_analytics: false,
    advanced_analytics: false,
    featured_placements: false,
    promotions: false,
    tournament_listings: false,
    priority_support: false,
  }),
  starter: Object.freeze({
    listing: true,
    bookings: true,
    basic_notifications: true,
    multi_court: true,
    basic_analytics: true,
    advanced_analytics: false,
    featured_placements: false,
    promotions: true,
    tournament_listings: false,
    priority_support: false,
  }),
  pro: Object.freeze({
    listing: true,
    bookings: true,
    basic_notifications: true,
    multi_court: true,
    basic_analytics: true,
    advanced_analytics: true,
    featured_placements: true,
    promotions: true,
    tournament_listings: true,
    priority_support: false,
  }),
  premium: Object.freeze({
    listing: true,
    bookings: true,
    basic_notifications: true,
    multi_court: true,
    basic_analytics: true,
    advanced_analytics: true,
    featured_placements: true,
    promotions: true,
    tournament_listings: true,
    priority_support: true,
  }),
});

/**
 * F40 — active-promo quota per tier (D1). Counts promos in non-terminal states
 * {draft, scheduled, active, paused} for a venue, evaluated against the venue's
 * effectiveTierCode. `null` = unlimited. Free is gated out of promotions entirely
 * (quota 0); Starter/Pro escalate; Premium is unlimited. The `promotions` feature
 * flag in TIER_FEATURE_FLAGS is the binary gate; this is the per-tier cap above it.
 */
export const TIER_ACTIVE_PROMO_QUOTA: Readonly<Record<SubscriptionTierCode, number | null>> =
  Object.freeze({
    free: 0,
    starter: 2,
    pro: 10,
    premium: null,
  });

/**
 * F41 — active-tournament quota per tier (D2). Counts a self-serve venue/club
 * organizer's tournaments in non-terminal publish states
 * {published, registration_open, registration_closed, live} for the organizer
 * account, evaluated against the billing venue's `effectiveTierCode`. `null` =
 * unlimited. Free + Starter have NO F41 access (quota 0 — the `tournament_listings`
 * feature flag in TIER_FEATURE_FLAGS stays Pro+, mirroring TIER_ACTIVE_PROMO_QUOTA).
 * Admin-approved federation organizers + admin-created tournaments are EXEMPT from
 * this quota entirely (§18.1 / D19) — the cap applies only to the self-serve path.
 */
export const TIER_ACTIVE_TOURNAMENT_QUOTA: Readonly<Record<SubscriptionTierCode, number | null>> =
  Object.freeze({
    free: 0,
    starter: 0,
    pro: 5,
    premium: null,
  });

/**
 * F41 — the one-time tournament-listing fee per tier (D3), USD minor units,
 * snapshotted onto the `tournaments.listing_fee_minor` row at publish (F35/F39
 * snapshot precedent — a later tier change never rewrites a published tournament's
 * economics). Reachable values are Pro $10 / Premium $0; `starter: 1500` ($15) is
 * retained defensively but UNREACHABLE behind the Pro+ publish gate. A `> 0` fee
 * appends an F23 `tournament_listing` line item; Premium ($0) appends none.
 */
export const FEATURED_TOURNAMENT_TIER_TERMS: Readonly<
  Record<SubscriptionTierCode, Readonly<{ listingFeeMinor: number }>>
> = Object.freeze({
  free: Object.freeze({ listingFeeMinor: 0 }),
  starter: Object.freeze({ listingFeeMinor: 1500 }), // $15 — unreachable behind the Pro+ gate (defensive).
  pro: Object.freeze({ listingFeeMinor: 1000 }), // $10
  premium: Object.freeze({ listingFeeMinor: 0 }), // $0
});

// ─── Constants ───────────────────────────────────────────────────────────────

/** Days from invoice issue to due_at (drives `subscription.invoice_overdue`). */
export const SUBSCRIPTION_INVOICE_DUE_GRACE_DAYS = 7 as const;
/** Days past the invoice due date before tier features lock down (D9). */
export const SUBSCRIPTION_PAST_DUE_GRACE_DAYS = 7 as const;
/** Trial length (D4 — 14-day Pro trial). */
export const SUBSCRIPTION_TRIAL_DURATION_DAYS = 14 as const;
/** Annual price multiplier (≈20% off — D3): annual = round(monthly × 12 × 0.8). */
export const SUBSCRIPTION_ANNUAL_DISCOUNT_FACTOR = 0.8 as const;
/** Tier-derived commission rate ceiling (mirrors F23 venue rate bounds). */
export const SUBSCRIPTION_MIN_RATE_BASIS_POINTS = 0 as const;
export const SUBSCRIPTION_MAX_RATE_BASIS_POINTS = 5000 as const;

/**
 * Seed for the `subscription_tiers` catalog (§0 pricing — ✅ FOUNDER).
 * All amounts USD minor units. `isTrialDefault` is true on `pro` only (D5).
 */
export type SubscriptionTierSeedRow = {
  readonly code: SubscriptionTierCode;
  readonly displayName: string;
  readonly sortOrder: number;
  readonly monthlyPriceAmount: number;
  readonly annualPriceAmount: number;
  readonly commissionBasisPoints: number;
  readonly isTrialDefault: boolean;
};

export const SUBSCRIPTION_TIER_SEED: ReadonlyArray<SubscriptionTierSeedRow> = [
  {
    code: 'free',
    displayName: 'Free',
    sortOrder: 0,
    monthlyPriceAmount: 0,
    annualPriceAmount: 0,
    commissionBasisPoints: 1000,
    isTrialDefault: false,
  },
  {
    code: 'starter',
    displayName: 'Starter',
    sortOrder: 1,
    monthlyPriceAmount: 1900,
    annualPriceAmount: 18240,
    commissionBasisPoints: 800,
    isTrialDefault: false,
  },
  {
    code: 'pro',
    displayName: 'Pro',
    sortOrder: 2,
    monthlyPriceAmount: 4900,
    annualPriceAmount: 47040,
    commissionBasisPoints: 500,
    isTrialDefault: true,
  },
  {
    code: 'premium',
    displayName: 'Premium',
    sortOrder: 3,
    monthlyPriceAmount: 9900,
    annualPriceAmount: 95040,
    commissionBasisPoints: 300,
    isTrialDefault: false,
  },
];

// ─── TierProvider contract (§3/§7) ───────────────────────────────────────────

/**
 * The resolved tier snapshot returned by `TierProvider.getVenueTier`. The
 * single source of truth F39/F40/F41 consume to gate features.
 *
 * `effectiveTierCode` may diverge from `tierCode` during past-due lock-down
 * (D9): when `status='past_due'` AND the 7-day grace expired,
 * `effectiveTierCode='free'` (features lock) while `tierCode` retains the
 * purchased tier (so the UI shows "Pro — past due, features locked").
 * `features` + `commissionBasisPoints` follow `effectiveTierCode`.
 */
export interface VenueTierResolution {
  readonly venueId: string;
  /** Purchased tier (what the venue pays for). */
  readonly tierCode: SubscriptionTierCode;
  readonly status: VenueSubscriptionStatus;
  /** Grace/lock-down → may be 'free' even if tierCode='pro' (D9). */
  readonly effectiveTierCode: SubscriptionTierCode;
  /** Resolved from `effectiveTierCode` via TIER_FEATURE_FLAGS. */
  readonly features: Readonly<Record<TierFeatureFlag, boolean>>;
  /** Tier-derived (D2); follows `effectiveTierCode` during lock-down. */
  readonly commissionBasisPoints: number;
  /** From F21 venue_payment_config (commission_only | subscription_only | hybrid). */
  readonly billingModel: BillingModel;
  readonly trialEndsAt: string | null;
  /** True when `status='past_due'` but still within the 7-day grace window. */
  readonly inGrace: boolean;
}

/**
 * F38-frozen interface contract (DO NOT change without escalating). F39/F40/F41
 * receive this via their service constructors — they never `new` the concrete
 * `DefaultTierProvider`. `StubTierProvider` returns Free for any venue and
 * never throws (defence-in-depth fallback).
 */
export interface TierProvider {
  getVenueTier(venueId: string): Promise<VenueTierResolution>;
}

// ─── Read-side projections ───────────────────────────────────────────────────

/**
 * One row of the `subscription_tiers` catalog (admin-editable pricing +
 * commission — D11). Feature flags are NOT stored; read from `TIER_FEATURE_FLAGS`.
 */
export type SubscriptionTier = {
  readonly code: SubscriptionTierCode;
  readonly displayName: string;
  readonly sortOrder: number;
  readonly monthlyPriceAmount: number;
  readonly annualPriceAmount: number;
  readonly priceCurrency: string;
  readonly commissionBasisPoints: number;
  readonly isTrialDefault: boolean;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * The `venue_subscriptions` per-venue assignment row (D10, one per venue).
 * Carries the current tier + status + trial + period bounds + any queued
 * switch (D8). `updatedAt` is the If-Match version source for the owner switch.
 */
export type VenueSubscription = {
  readonly id: string;
  readonly venueId: string;
  readonly tierCode: SubscriptionTierCode;
  readonly billingCadence: BillingCadence;
  readonly status: VenueSubscriptionStatus;
  readonly trialEndsAt: string | null;
  /** D12 — once-per-venue trial eligibility (true once a trial has started). */
  readonly trialConsumed: boolean;
  readonly currentPeriodStart: string;
  readonly currentPeriodEnd: string;
  /** D8 — pending tier (null = no queued switch). */
  readonly queuedSwitchTo: SubscriptionTierCode | null;
  readonly queuedSwitchCadence: BillingCadence | null;
  readonly queuedEffectiveAt: string | null;
  /** D-cancel — reverts to Free at period end. */
  readonly cancelAtPeriodEnd: boolean;
  /** Set when an invoice goes overdue; grace = +7 days (D9). */
  readonly pastDueSince: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * The owner-facing subscription detail returned by the lifecycle service +
 * `GET /me/venues/:venueId/subscription`. Bundles the current subscription
 * row, the resolved queued switch (if any), and the full tier catalog so the
 * portal pricing page can render comparison cards in one round-trip.
 *
 * `queuedSwitch` mirrors the three `queued*` columns on `VenueSubscription`
 * into a single nullable object for ergonomic client rendering (it is `null`
 * when no switch is queued).
 */
export type QueuedSubscriptionSwitch = {
  readonly tierCode: SubscriptionTierCode;
  readonly cadence: BillingCadence;
  readonly effectiveAt: string;
};

export type VenueSubscriptionDetail = {
  readonly subscription: VenueSubscription;
  readonly queuedSwitch: QueuedSubscriptionSwitch | null;
  /** The 4-tier catalog (admin-editable pricing + commission), sort-ordered. */
  readonly tiers: ReadonlyArray<SubscriptionTier>;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * `POST /me/venues/:venueId/subscription/switch` body. Queues to the next
 * period boundary (D8) OR effective-now if during a trial→upgrade. `If-Match:
 * <venue_subscriptions.updated_at>` header required. Advisory-lock serialized.
 */
export const SwitchSubscriptionInputSchema = z
  .object({
    tierCode: SubscriptionTierCodeSchema,
    cadence: BillingCadenceSchema,
  })
  .strict();
export type SwitchSubscriptionInput = z.infer<typeof SwitchSubscriptionInputSchema>;

/** `POST /me/venues/:venueId/subscription/cancel` body (D-cancel). */
export const CancelSubscriptionInputSchema = z.object({}).strict();
export type CancelSubscriptionInput = z.infer<typeof CancelSubscriptionInputSchema>;

/** `POST /me/venues/:venueId/subscription/start-trial` body (D5/D12). */
export const StartTrialInputSchema = z.object({}).strict();
export type StartTrialInput = z.infer<typeof StartTrialInputSchema>;

/**
 * `PUT /admin/venue-subscriptions/:venueId/override` body. Overrides
 * tier/cadence/status with a mandatory reason → appends history + a rate row.
 * At least one of tierCode / cadence / status must be present.
 */
export const AdminOverrideSubscriptionInputSchema = z
  .object({
    tierCode: SubscriptionTierCodeSchema.optional(),
    cadence: BillingCadenceSchema.optional(),
    status: VenueSubscriptionStatusSchema.optional(),
    reason: z.string().trim().min(10).max(500),
  })
  .strict()
  .refine((v) => v.tierCode !== undefined || v.cadence !== undefined || v.status !== undefined, {
    message: 'At least one of tierCode, cadence, or status must be provided',
  });
export type AdminOverrideSubscriptionInput = z.infer<typeof AdminOverrideSubscriptionInputSchema>;

/**
 * `POST /admin/venue-subscriptions/:venueId/grant-trial` body. Case-by-case
 * admin trial grant/extend (resets `trial_consumed` per admin judgment, D6).
 */
export const GrantTrialInputSchema = z
  .object({
    reason: z.string().trim().min(10).max(500),
    trialDays: z.number().int().min(1).max(365).optional(),
  })
  .strict();
export type GrantTrialInput = z.infer<typeof GrantTrialInputSchema>;

/**
 * `POST /admin/venue-subscriptions/:venueId/manual-invoice` body. One-off
 * charge (manual `commission_statements` row, source `venue_subscription`).
 */
export const ManualSubscriptionInvoiceInputSchema = z
  .object({
    amount: z.number().int().min(0),
    reason: z.string().trim().min(10).max(500),
  })
  .strict();
export type ManualSubscriptionInvoiceInput = z.infer<typeof ManualSubscriptionInvoiceInputSchema>;

/**
 * `PUT /admin/subscription-tiers/:code` body. Edits monthly/annual price +
 * commission bp (NOT feature gating — D11). `If-Match` + mandatory reason.
 */
export const EditTierPricingInputSchema = z
  .object({
    monthlyPriceAmount: z.number().int().min(0),
    annualPriceAmount: z.number().int().min(0),
    commissionBasisPoints: z
      .number()
      .int()
      .min(SUBSCRIPTION_MIN_RATE_BASIS_POINTS)
      .max(SUBSCRIPTION_MAX_RATE_BASIS_POINTS),
    reason: z.string().trim().min(10).max(500),
  })
  .strict();
export type EditTierPricingInput = z.infer<typeof EditTierPricingInputSchema>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F38 subscription error vocabulary. Service maps to HTTP:
 *   - `*_NOT_FOUND` → 404
 *   - `*_STALE` → 409 (If-Match mismatch)
 *   - `*_FORBIDDEN` → 403
 *   - everything else → 422
 */
export const SUBSCRIPTION_ERROR_CODES = [
  'SUBSCRIPTION_NOT_FOUND',
  'SUBSCRIPTION_STALE',
  'SUBSCRIPTION_SWITCH_ALREADY_QUEUED',
  'SUBSCRIPTION_SWITCH_NO_CHANGE',
  // FIX 5.23 (§13.11-4) — a manual invoice for a period already invoiced.
  'SUBSCRIPTION_INVOICE_EXISTS',
  'SUBSCRIPTION_SWITCH_NOTHING_TO_CANCEL',
  'SUBSCRIPTION_TRIAL_ALREADY_CONSUMED',
  'SUBSCRIPTION_TIER_NOT_FOUND',
  'SUBSCRIPTION_TIER_PRICE_INVALID',
  'SUBSCRIPTION_ADMIN_OVERRIDE_REASON_REQUIRED',
  'FEATURE_GATE_BLOCKED',
  'SUBSCRIPTION_NOT_PAST_DUE',
  'SUBSCRIPTION_FORBIDDEN',
] as const;
export type SubscriptionErrorCode = (typeof SUBSCRIPTION_ERROR_CODES)[number];
